import { Box, Modal } from '@hover/blueprint';
import { iLink2 } from '@hover/icons';
import React from 'react';
import { useParams } from 'react-router';

import { CancelButton } from 'src/modules/renderings/components/ShareModal/CancelButton';
import { makeValidationSchema } from 'src/modules/renderings/components/common/makeValidationSchema';
import { useRenderingContext } from 'src/modules/renderings/hooks';
import { useShareByEmail } from 'src/modules/renderings/hooks/useShareByEmail';
import { Form, TextField } from 'src/shared/components/forms';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import {
  TranslationNamespace,
  useCurrentPage,
  useTranslate,
} from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

import { ConfirmButton } from './ConfirmButton';
import { ConsentCheckbox } from './ConsentCheckbox';
import { CopyLinkButton } from './CopyLinkButton';
import { copyLinkViaBrowserAPI, useConfiguredToast } from './shareHelpers';

interface ReShareModalProps {
  isConsentChecked: boolean;
  handleConsentChecked: () => void;
  isModalOpen: boolean;
  handleCloseButton: () => void;
}

const ReShareModal = ({
  isConsentChecked,
  handleConsentChecked,
  isModalOpen,
  handleCloseButton,
}: ReShareModalProps) => {
  const currentPage = useCurrentPage();
  const toast = useConfiguredToast();
  const { trackingData } = useRenderingContext();
  const t = useTranslate(TranslationNamespace.SHARED);
  const { shareToken } = useParams<{ shareToken?: string }>();

  const c = useTranslate(TranslationNamespace.COMMON);
  const validationSchema = makeValidationSchema(c);

  const { setShareEmail, setShowEmailError, isEmailShareLoading, shareEmail } =
    useShareByEmail();

  const isConfirmDisabled = !isConsentChecked || !shareToken;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShareEmail(e.target.value);
    setShowEmailError(false);
  };

  const handleCopyLink = async () => {
    try {
      await copyLinkViaBrowserAPI(window.location.href);
      toast({
        description: t('renderings.messages.copyLink.success'),
        status: 'success',
      });
    } catch (error) {
      logError(error as Error);
      toast({
        description: t('renderings.messages.copyLink.fail'),
        status: 'error',
      });
    }
  };

  const onClose = () => {
    const properties = {
      ...trackingData,
      page_or_screen_name: currentPage ?? '',
      primary_cta: false,
      button_text: 'x',
      feature: FeatureTracking.RENDERING,
      track_id: 'rendering-share-cancel',
    };
    buttonPressed(properties);
    handleCloseButton();
  };

  return (
    <Modal
      aria-label="shareModal"
      header={t('renderings.shareRenderings')}
      isOpen={isModalOpen}
      onClose={onClose}
      size="medium"
    >
      <Form
        initialValues={{ emailAddress: '' }}
        onSubmit={() => {
          if (isConsentChecked) {
            window.location.href = `mailto:${shareEmail}?subject=${encodeURIComponent(
              t('renderings.messages.emailShare.subject'),
            )}&body=${window.location.href}`;
          }
        }}
        validationSchema={validationSchema}
      >
        <TextField
          isDisabled={isEmailShareLoading}
          label={t('renderings.emailAddressLabel')}
          name="emailAddress"
          onChange={handleChange}
          placeholder={t('renderings.emailAddressField')}
          testId="emailTextField"
          type="email"
        />
        <ConsentCheckbox
          isChecked={isConsentChecked}
          onChange={handleConsentChecked}
          reShare
          testId="share-consent-checkbox"
        />
        <Box
          alignItems="center"
          justifyContent="space-between"
          paddingTop={400}
        >
          <CopyLinkButton
            iconAfter={iLink2}
            isDisabled={!isConsentChecked}
            onClick={handleCopyLink}
            testId="copy-link"
          />
          <Box gap={300}>
            <CancelButton onClick={handleCloseButton} padding="12px 24px">
              {t('renderings.cancel')}
            </CancelButton>
            <ConfirmButton
              isDisabled={isConfirmDisabled}
              isLoading={isEmailShareLoading}
              padding="12px 24px"
              testId="shareWithEmailButton"
            >
              {shareToken
                ? t('renderings.shareRenderings')
                : t('renderings.messages.generateLink.inProgress')}
            </ConfirmButton>
          </Box>
        </Box>
      </Form>
    </Modal>
  );
};

export { ReShareModal };
